import { useState } from "react";
import { showSuccess, showError, showApiError } from "@/lib/utils/notifications";
import { useBankLinking } from "@/hooks/useBankLinking";
import {
  createNinjaIbvRequest,
  createNinjaLinkToken,
} from "@/lib/api/bankService";

export function useIncomeTabBankLinking(options: {
  applicantId: string;
}) {
  const { applicantId } = options;

  const {
    ninjaAccounts,
    ninjaConfig,
    isLoading: isBankLinkingLoading,
    refreshAccounts,
  } = useBankLinking({
    applicantId,
    onSuccess: (type) => {
      showSuccess(`Bank functionality ${type.replace("_", " ")} successfully`);
    },
    onError: (err) => {
      showError(err);
    },
  });

  const [showNinjaIframePopup, setShowNinjaIframePopup] = useState(false);
  const [ninjaIbvResponseData, setNinjaIbvResponseData] = useState<Record<
    string,
    unknown
  > | null>(null);
  const [isOpeningNinjaBankLink, setIsOpeningNinjaBankLink] = useState(false);

  const handleLinkBank = async () => {
    if (!applicantId) {
      showError("Applicant ID is required");
      return;
    }
    if (!ninjaConfig) {
      showError("Bank linking is not available. Please contact support.");
      return;
    }
    setIsOpeningNinjaBankLink(true);
    try {
      // Angular: create_link_token on load, then ibv_request on click.
      // React creates the token on click so the session is always fresh.
      await createNinjaLinkToken(applicantId);
      const ibvResponse = await createNinjaIbvRequest(applicantId);
      // Angular opens the popup only when both link + product_request_id exist.
      if (!ibvResponse?.ibv_connection_link || !ibvResponse?.product_request_id) {
        throw new Error("Unable to open bank link. Please try again.");
      }
      setNinjaIbvResponseData(ibvResponse);
      setShowNinjaIframePopup(true);
    } catch (error: unknown) {
      // Angular openaccountninja: notify(error.error.message || error.error, 0)
      // bankService throws a plain { message, data } ApiError (not Error).
      showApiError(error);
    } finally {
      setIsOpeningNinjaBankLink(false);
    }
  };

  return {
    ninjaAccounts,
    ninjaConfig,
    isBankLinkingLoading,
    refreshAccounts,
    showNinjaIframePopup,
    setShowNinjaIframePopup,
    ninjaIbvResponseData,
    setNinjaIbvResponseData,
    isOpeningNinjaBankLink,
    handleLinkBank,
  };
}
